import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { CybersourceMicroformForm } from './CybersourceMicroformForm';
import useCybersourceMicroformForm from './useCybersourceMicroformForm';
import { CybersourceMicroformFormProvider } from './CybersourceMicroformFormProvider';

/**
 * CybersourcePaymentForm component.
 * Renders a complete payment form with Cybersource Microform integration.
 *
 * @param {object} props - Component props.
 * @param {Function} props.onSubmit - Function called when form is submitted with payment data.
 * @param {string} props.billingAddress - Optional billing address.
 * @returns {React.ReactElement} The CybersourcePaymentForm component.
 */
const CybersourcePaymentForm = React.memo(({ onSubmit, billingAddress, onFormErrors }) => {
  const [formValues, setFormValues] = useState({
    cardNumberValid: false,
    cardSecurityCodeValid: false,
    cardBrand: '',
    cybsCardType: '',
    holderName: '',
    expirationMonth: '',
    expirationYear: '',
    billingAddress: billingAddress || '',
  });
  const [formState, setFormState] = useState({
    errors: {
      holderName: false,
      expirationMonth: false,
      expirationYear: false,
      cardNumber: false,
      securityCode: false,
    },
    touched: {
      holderName: false,
      expirationMonth: false,
      expirationYear: false,
      cardNumber: false,
      securityCode: false,
    },
  });

  const { createPaymentPayload } = useCybersourceMicroformForm();

  // Utility functions for field validation
  const isFieldInvalid = useCallback(
    (fieldName) => {
      return formState.errors[fieldName];
    },
    [formState.errors],
  );

  const isFieldTouched = useCallback(
    (fieldName) => {
      return formState.touched[fieldName];
    },
    [formState.touched],
  );

  // Function to determine if a field should show error
  const shouldShowError = useCallback(
    (fieldName) => {
      return isFieldInvalid(fieldName) && isFieldTouched(fieldName);
    },
    [isFieldInvalid, isFieldTouched],
  );

  // Mark a field as touched
  const markFieldAsTouched = useCallback((fieldName) => {
    setFormState((prevState) => ({
      ...prevState,
      touched: {
        ...prevState.touched,
        [fieldName]: true,
      },
    }));
  }, []);

  // Function to validate a specific field
  const validateField = useCallback((fieldName, value) => {
    let isValid = true;

    // Determine validity based on field type
    if (fieldName === 'holderName') {
      isValid = !!value;
    } else if (fieldName === 'expirationMonth') {
      isValid = !!value;
    } else if (fieldName === 'expirationYear') {
      isValid = !!value;
    } else if (fieldName === 'cardNumberValid') {
      isValid = !!value;
      fieldName = 'cardNumber'; // Map to error field name
    } else if (fieldName === 'cardSecurityCodeValid') {
      isValid = !!value;
      fieldName = 'securityCode'; // Map to error field name
    }

    // Update error state for this field only
    setFormState((prevState) => ({
      ...prevState,
      errors: {
        ...prevState.errors,
        [fieldName]: !isValid,
      },
    }));

    return isValid;
  }, []);

  // Function to update form values and validate
  const handleFieldChange = useCallback(
    (fieldName, value) => {
      // Update form value
      setFormValues((prev) => ({
        ...prev,
        [fieldName]: value,
      }));

      // Validate the field with the new value
      validateField(fieldName, value);
    },
    [validateField],
  );

  // Validate individual form fields
  const validateFields = useCallback(
    (markAllTouched = false) => {
      const errors = {
        holderName: !formValues.holderName,
        expirationMonth: !formValues.expirationMonth,
        expirationYear: !formValues.expirationYear,
        cardNumber: !formValues.cardNumberValid,
        securityCode: !formValues.cardSecurityCodeValid,
      };

      // Check if we have any errors
      const hasErrors = Object.values(errors).some((hasError) => hasError);

      // Update form state with new errors and optionally mark all fields as touched
      setFormState((prevState) => {
        const newState = {
          errors,
          touched: { ...prevState.touched },
        };

        // If markAllTouched is true or during form submission, mark all fields as touched
        if (markAllTouched) {
          Object.keys(newState.touched).forEach((key) => {
            newState.touched[key] = true;
          });
        }

        return newState;
      });

      // Return both the validity status and the errors object
      return { isValid: !hasErrors, errors };
    },
    [formValues],
  );

  // Handle form submission
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // Validate all fields and mark all as touched to show errors
      const { isValid, errors } = validateFields(true); // true = mark all fields as touched

      if (!isValid) {
        // If form is invalid, collect error fields from the validation result
        const errorFields = Object.entries(errors)
          .filter(([, hasError]) => hasError)
          .reduce((obj, [field]) => {
            obj[field] = true;
            return obj;
          }, {});

        if (onFormErrors) {
          onFormErrors(errorFields, formValues);
        }
        return;
      }

      try {
        const payload = await createPaymentPayload({
          holderName: formValues.holderName,
          expirationMonth: formValues.expirationMonth,
          expirationYear: formValues.expirationYear,
          cardBrand: formValues.cardBrand,
          cybsCardType: formValues.cybsCardType,
        });

        if (onSubmit) {
          onSubmit({
            ...payload,
            billingAddress: formValues.billingAddress,
          });
        }
      } catch (error) {
        console.error('Payment submission error:', error);
      }
    },
    [formValues, createPaymentPayload, validateFields, onSubmit, onFormErrors],
  );

  // Prevent accidental form submission and dismiss mobile keyboard on Enter key
  const handleKeyDown = useCallback((e) => {
    if (e.key !== 'Enter') return;

    if (e.nativeEvent?.isComposing) return;

    // Prevent accidental Enter submit only from single-line text inputs
    if (e.target?.tagName === 'INPUT') {
      e.preventDefault();
      if (document.activeElement) {
        document.activeElement.blur();
      }
    }
  }, []);

  return (
    <div className="cybersource-payment-form">
      <form
        id="cybersource-payment-form"
        onSubmit={handleSubmit}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      >
        <CybersourceMicroformForm
          billingAddress={billingAddress}
          fieldErrors={formState.errors}
          shouldShowError={shouldShowError}
          onFieldBlur={markFieldAsTouched}
          formValues={formValues}
          onFieldChange={handleFieldChange}
        />
      </form>
    </div>
  );
});

CybersourcePaymentForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onFormErrors: PropTypes.func,
  billingAddress: PropTypes.string,
};

/**
 * CybersourcePaymentFormWrapper component.
 * Wraps the CybersourcePaymentForm with its provider.
 *
 * @param {object} props - Component props.
 * @param {Function} props.onSubmit - Function called when form is submitted with payment data.
 * @param {string} props.billingAddress - Optional billing address.
 * @returns {React.ReactElement} The wrapped CybersourcePaymentForm component.
 */
const CybersourcePaymentFormWrapper = React.memo(({ onSubmit, billingAddress, onFormErrors }) => {
  return (
    <CybersourceMicroformFormProvider>
      <CybersourcePaymentForm
        onSubmit={onSubmit}
        billingAddress={billingAddress}
        onFormErrors={onFormErrors}
      />
    </CybersourceMicroformFormProvider>
  );
});

CybersourcePaymentFormWrapper.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  billingAddress: PropTypes.string,
  onFormErrors: PropTypes.func,
};

export default CybersourcePaymentFormWrapper;
